// src/components/VideoCard.js
import React, { useState, useRef, useEffect } from 'react';
import { relativeDayLabel } from '../utils/timezone';
import CompletionHistoryList from './CompletionHistoryList';

// Deep muted teal for the "Strong+" difficulty tag — deliberately distinct from
// the sage used by "Ease In" (--mom-success) and matched to the "From light to
// strong" program artwork so the heavier-lifting cue reads consistently across
// programs and classes. Intentionally a literal (not a theme token): the tag
// taxonomy is fixed and not admin-themed.
const STRONG_PLUS_COLOR = '#4f7c7a';

function VideoCard({
    video, onClick, animationDelay = 0, vertical = false, isCompleted = false,
    onToggleComplete, showFreeLabel = false, isLocked = false, favoriteCount = 0, airedLabel = null,
    // Event-log completion model (see adr-completions-event-log.md). When
    // onLogComplete is provided, the control becomes an ADDITIVE "Log this workout"
    // (a re-log credits a repeat) with a short Undo + a per-video history line.
    // When it is absent, the legacy onToggleComplete toggle is used unchanged.
    // onLoadHistory(video) resolves the id-backed session list just-in-time for
    // the expandable "Your history" remover (see docs/ux/completion-undo.md).
    completionCount = 0, lastCompletedAt = null, onLogComplete, onUndoComplete, onLoadHistory, completionCopy = {},
}) {
    const [hovered, setHovered] = useState(false);
    const [imgFailed, setImgFailed] = useState(false);
    const [justCompleted, setJustCompleted] = useState(false);
    const [logging, setLogging] = useState(false);
    const [justLoggedId, setJustLoggedId] = useState(null); // doc id eligible for Undo
    // Persistent "Your history" disclosure — the per-entry completion remover,
    // identical to the player modal (see docs/ux/completion-undo.md). Ids are
    // resolved just-in-time on expand via onLoadHistory.
    const [historyOpen, setHistoryOpen] = useState(false);
    const [historyList, setHistoryList] = useState(null); // null = not yet loaded
    const [historyLoading, setHistoryLoading] = useState(false);
    const undoTimerRef = useRef(null);
    const celebrateTimerRef = useRef(null);
    useEffect(() => () => {
        if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
        if (celebrateTimerRef.current) clearTimeout(celebrateTimerRef.current);
    }, []);
    const groupLabel = (g) => ({ upper: 'Upper', lower: 'Lower', core: 'Core', stretch: 'Stretch' }[g] || g);
    const isClassRecording = video?.isClassRecording || false;

    // ── Legacy toggle path (no onLogComplete) ──
    const handleComplete = (e) => {
        e.stopPropagation();
        if (!isCompleted) {
            setJustCompleted(true);
            setTimeout(() => {
                onToggleComplete(video);
                setJustCompleted(false);
            }, 800);
        } else {
            onToggleComplete(video);
        }
    };

    // ── Event-log path (additive "Log this workout") ──
    const handleLog = async (e) => {
        e.stopPropagation();
        if (logging) return;
        setLogging(true);
        setJustCompleted(true); // reuse the celebratory check-pop overlay
        if (celebrateTimerRef.current) clearTimeout(celebrateTimerRef.current);
        celebrateTimerRef.current = setTimeout(() => setJustCompleted(false), 650);
        let rec = null;
        try { rec = await onLogComplete(video); } catch (_) { rec = null; }
        setLogging(false);
        if (rec && rec.id) {
            setJustLoggedId(rec.id);
            if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
            undoTimerRef.current = setTimeout(() => setJustLoggedId(null), 6000);
            // Invalidate the cached history so the next "Your history" open
            // refetches and includes this new session.
            setHistoryList(null);
        }
    };
    const handleUndo = (e) => {
        e.stopPropagation();
        if (!justLoggedId) return;
        const id = justLoggedId;
        if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
        setJustLoggedId(null);
        if (typeof onUndoComplete === 'function') onUndoComplete(video, id);
    };

    // ── Persistent "Your history" disclosure ──
    // Lazy-loads the id-backed session list on expand (onLoadHistory), then the
    // shared CompletionHistoryList renders per-entry confirm + Remove. Removal
    // delegates to the parent's onUndoComplete (delete + count decrement) and
    // splices the local list. stopPropagation everywhere so the card's onClick
    // never fires from inside the history UI.
    const toggleHistory = async (e) => {
        if (e && e.stopPropagation) e.stopPropagation();
        if (historyOpen) { setHistoryOpen(false); return; }
        setHistoryOpen(true);
        if (historyList === null && !historyLoading && typeof onLoadHistory === 'function') {
            setHistoryLoading(true);
            let list = [];
            try { list = await onLoadHistory(video); } catch (_) { list = []; }
            setHistoryList(list || []);
            setHistoryLoading(false);
        }
    };
    const handleRemoveEntry = async (entryId) => {
        if (typeof onUndoComplete !== 'function') return false;
        let ok = false;
        try { ok = await onUndoComplete(video, entryId); } catch (_) { ok = false; }
        if (ok) setHistoryList((prev) => (prev || []).filter((e) => e.id !== entryId));
        return ok;
    };

    const useLogModel = typeof onLogComplete === 'function';
    const when = relativeDayLabel(lastCompletedAt);
    const logLabel = completionCount > 0
        ? (completionCopy.logAgainLabel || 'Log again')
        : (completionCopy.logLabel || 'Log this workout');

    return (
        <div className="slide-up mom-video-card" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
            style={{
                background: 'linear-gradient(180deg, #ffffff 0%, #fbfcfd 40%, #f6f8fa 100%)',
                borderRadius: 20,
                overflow: 'hidden',
                transition: justCompleted ? 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: hovered ? 'var(--shadow-lg)' : 'var(--shadow-md)',
                transform: justCompleted ? 'scale(0.96)' : hovered ? 'translateY(-4px)' : 'none',
                opacity: justCompleted ? 0.5 : isCompleted ? 0.75 : isLocked ? 0.55 : 1,
                animationDelay: `${animationDelay}s`,
                position: 'relative',
                display: 'flex', flexDirection: 'column', height: '100%',
                border: '1px solid rgba(255,255,255,0.5)',
                contain: 'content',
            }}>

            {/* Specular highlight */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '50%', background: 'radial-gradient(ellipse 70% 80% at 25% 0%, rgba(255,255,255,0.14) 0%, transparent 60%)', pointerEvents: 'none', zIndex: 1, borderRadius: '20px 20px 0 0' }} />

            {/* Completion overlay animation */}
            {justCompleted && (
                <div style={{
                    position: 'absolute', inset: 0, zIndex: 20, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(255,255,255,0.85)', borderRadius: 20,
                    animation: 'completeFadeIn 0.3s ease-out',
                }}>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{
                            width: 56, height: 56, borderRadius: '50%',
                            background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                            color: 'var(--mom-white)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 28, margin: '0 auto 12px',
                            animation: 'checkPop 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.1s both',
                            boxShadow: '0 4px 16px color-mix(in srgb, var(--mom-primary) 35%, transparent)',
                        }}>✓</div>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: 'var(--mom-charcoal)' }}>Done!</p>
                    </div>
                </div>
            )}

            <div onClick={() => onClick && onClick(video)} style={{ cursor: onClick ? 'pointer' : 'default', flex: 1 }}>
                <div style={{ height: (video.driveThumbnail && !imgFailed) ? 'auto' : (vertical ? 280 : 180), background: 'linear-gradient(135deg, var(--mom-cream), var(--mom-cream-dark))', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
                    {video.driveThumbnail && !imgFailed ? (
                        <img src={video.driveThumbnail} alt={video.title} style={{ width: '100%', height: 'auto', display: 'block' }} onError={() => setImgFailed(true)} crossOrigin="anonymous" />
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', background: 'linear-gradient(135deg, var(--mom-charcoal), var(--mom-charcoal-light))', padding: 20 }}>
                            <span style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 'clamp(16px, 3vw, 22px)', fontWeight: 700, fontStyle: 'italic', color: 'var(--mom-white)', textAlign: 'center', lineHeight: 1.2 }}>moments of movement</span>
                            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 'clamp(8px, 1.5vw, 11px)', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>with Danielle</span>
                        </div>
                    )}
                    <div style={{ position: 'absolute', bottom: 12, right: 12, background: 'var(--mom-charcoal)', color: 'var(--mom-white)', fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 20 }}>{video.length} min</div>
                    {/* Muscle group badges removed — filters handle discoverability without crowding thumbnails */}
                    {isCompleted && !justCompleted && (
                        <div style={{ position: 'absolute', top: 12, left: 12, background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))', color: 'var(--mom-white)', fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700, padding: '4px 12px', borderRadius: 20, letterSpacing: '0.04em' }}>COMPLETED</div>
                    )}
                    {/* Aired badge (On Demand only — passed by the Classes tab). Takes the
                        top-left slot IN PLACE OF the FREE/CLASS tag, styled to match the
                        Schedule tab's "AIRED" treatment so the two surfaces read as one
                        system. Absent (Shorts / Free / every other usage) ⇒ FREE/CLASS
                        render exactly as before — zero change. COMPLETED still wins when
                        the class is completed (this is gated on !isCompleted). */}
                    {showFreeLabel && !isCompleted && !airedLabel && (
                        <div style={{ position: 'absolute', top: 12, left: 12, background: 'var(--mom-success, #6b9b7a)', color: '#fff', fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700, padding: '4px 12px', borderRadius: 20, letterSpacing: '0.04em' }}>FREE</div>
                    )}
                    {isClassRecording && !isCompleted && !showFreeLabel && !airedLabel && (
                        <div style={{ position: 'absolute', top: 12, left: 12, background: 'rgba(0,0,0,0.6)', color: '#fff', fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700, padding: '4px 12px', borderRadius: 20, letterSpacing: '0.04em', backdropFilter: 'blur(4px)' }}>CLASS</div>
                    )}
                    {airedLabel && !isCompleted && (
                        <div style={{ position: 'absolute', top: 12, left: 12, background: 'rgba(255,255,255,0.94)', backdropFilter: 'blur(8px)', color: 'var(--mom-charcoal)', fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700, padding: '4px 11px', borderRadius: 8, letterSpacing: '0.08em', textTransform: 'uppercase', boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }}>{airedLabel}</div>
                    )}
                    {(video?.easeIn || video?.strongPlus) && (
                        <div style={{ position: 'absolute', top: 12, right: 12, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                            {video?.easeIn && (
                                <div style={{ background: 'var(--mom-success, #6b9b7a)', color: '#fff', fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700, padding: '4px 12px', borderRadius: 20, letterSpacing: '0.04em' }}>EASE IN</div>
                            )}
                            {video?.strongPlus && (
                                <div style={{ background: STRONG_PLUS_COLOR, color: '#fff', fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700, padding: '4px 12px', borderRadius: 20, letterSpacing: '0.04em' }}>STRONG+</div>
                            )}
                        </div>
                    )}
                    {isLocked && (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)', borderRadius: 0, zIndex: 5 }}>
                            <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--mom-charcoal)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                </svg>
                            </div>
                        </div>
                    )}
                    {onClick && !isLocked && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: hovered ? 'rgba(0,0,0,0.12)' : 'transparent', transition: 'background 0.3s' }}>
                        <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'rgba(255,255,255,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: hovered ? 1 : 0, transition: 'opacity 0.3s', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
                            <span style={{ fontSize: 20, marginLeft: 3, color: 'var(--mom-charcoal)' }}>▶</span>
                        </div>
                    </div>}
                </div>
                <div className="mom-vc-body" style={{ padding: '14px 20px 10px' }}>
                    <h3 className="mom-vc-title" style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 17, marginBottom: 0, color: 'var(--mom-charcoal)' }}>{video.title}</h3>
                    {favoriteCount > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="var(--mom-primary)" stroke="none">
                                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                            </svg>
                            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: 'var(--mom-warm-gray)' }}>{favoriteCount}</span>
                        </div>
                    )}
                    {/* Per-video history (Peloton-style) — only in the event-log model */}
                    {useLogModel && completionCount > 0 && when && (
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 8 }}>
                            {completionCount > 1 ? (
                                <>
                                    <span style={{ fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontWeight: 700, fontSize: 15, color: 'var(--mom-primary)' }}>{completionCount}×</span>
                                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-warm-gray)' }}>· last {when}</span>
                                </>
                            ) : (
                                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-warm-gray)' }}>Completed · {when}</span>
                            )}
                        </div>
                    )}
                    {/* Persistent "Your history" — expandable per-entry remover,
                        identical to the player modal. Only when there's history to
                        show and the parent wired the loader + remover. No resting
                        clutter beyond the quiet disclosure link. */}
                    {useLogModel && completionCount > 0 && typeof onLoadHistory === 'function' && typeof onUndoComplete === 'function' && (
                        <div style={{ marginTop: 6 }}>
                            <button onClick={toggleHistory} className="mom-btn"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: 'var(--mom-warm-gray)', textDecoration: 'underline', padding: 0 }}>
                                {(completionCopy.historyDisclosureLabel || 'Your history')} {historyOpen ? '▴' : '▾'}
                            </button>
                            {historyOpen && (
                                <CompletionHistoryList
                                    sessions={historyList}
                                    loading={historyLoading}
                                    copy={completionCopy}
                                    onRemove={handleRemoveEntry}
                                />
                            )}
                        </div>
                    )}
                </div>
            </div>
            {/* Completion action — additive log (event-log model) or legacy toggle */}
            {(useLogModel || onToggleComplete) && (
                <div className="mom-vc-actions" style={{ padding: '0 20px 18px' }}>
                    {useLogModel ? (
                        justLoggedId ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '9px 14px', borderRadius: 12, border: '1.5px solid var(--mom-cream-dark)', background: 'var(--mom-cream)' }}>
                                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: 'var(--mom-primary)' }}>{completionCopy.loggedLabel || 'Logged'} ✓</span>
                                <button onClick={handleUndo} className="mom-btn" style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: 'var(--mom-warm-gray)', textDecoration: 'underline', padding: 0 }}>
                                    {completionCopy.undoLabel || 'Undo'}
                                </button>
                            </div>
                        ) : (
                            <button onClick={handleLog} disabled={logging} className="mom-btn"
                                style={{
                                    width: '100%', padding: '10px', borderRadius: 12, fontSize: 13, fontWeight: 600,
                                    fontFamily: 'var(--mom-font-sans)', cursor: logging ? 'default' : 'pointer',
                                    border: '1.5px solid var(--mom-primary)', background: 'transparent',
                                    color: 'var(--mom-primary)', transition: 'all 0.15s ease', opacity: logging ? 0.6 : 1,
                                }}>
                                {logLabel}
                            </button>
                        )
                    ) : (
                        <button onClick={handleComplete} disabled={justCompleted}
                            className="mom-btn"
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: 12,
                                fontSize: 13,
                                fontWeight: 600,
                                fontFamily: 'var(--mom-font-sans)',
                                cursor: justCompleted ? 'default' : 'pointer',
                                border: isCompleted ? '1.5px solid var(--mom-cream-dark)' : '1.5px solid var(--mom-primary)',
                                background: isCompleted ? 'var(--mom-cream)' : 'transparent',
                                color: isCompleted ? 'var(--mom-warm-gray)' : 'var(--mom-primary)',
                                transition: 'all 0.15s ease',
                            }}>
                            {isCompleted ? '✓  Completed' : 'Mark Complete'}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

export default VideoCard;